import { until, plural } from '../../../nucleus/format';
import { leftOutOf, listWords, outcomeOf, FREQUENCIES, describeCron } from './words';

// THE TOOL SHEET'S, THE RUNS PAGE'S AND THE REQUEST SHEET'S OWN WORDS AND
// SHAPES (plugin lifecycle §3.1, §3.5, §3.7, §3.8): what a run's answer says
// (its receipt), what a realm lets a tool write, a parked run's standing, and
// the runs page's facets. Pure: read from the server's answers, never worked
// out from flags.

const list = (l) => (Array.isArray(l) ? l : []);
const sameTool = (a, b) => String(a ?? '') === String(b ?? '');

// ── a realm, for one tool ────────────────────────────────────────────────────

// the server's "create note" said as a person reads it: creates note
const VERB = {
    create: ['creates', 'create'],
    edit: ['edits', 'edit'],
    update: ['edits', 'edit'],
    delete: ['deletes', 'delete'],
    propose: ['proposes', 'propose'],
    link: ['links', 'link'],
    relate: ['links', 'link'],
};
function said(entry, form) {
    const [verb, ...rest] = String(entry || '').trim().split(/\s+/);
    const words = VERB[verb];
    if (!words) return String(entry || '').trim();
    return [words[form], ...rest].join(' ');
}

/** What a realm lets one tool write, for a picker's hint: "creates diary-entry ·
 *  can't create note". null when the server did not say (an older one). */
export function realmHint(realm, toolId) {
    if (!realm || (!realm.writes && !realm.cannot_write)) return null;
    const key = toolId ?? '';
    const writes = list(realm.writes?.[key]);
    const cannot = list(realm.cannot_write?.[key]);
    if (!writes.length && !cannot.length) return null;
    return [
        writes.length && listWords(writes.map((w) => said(w, 0))),
        cannot.length && `can’t ${listWords(cannot.map((w) => said(w, 1)))}`,
    ].filter(Boolean).join(' · ');
}

/** What a tool writes, by schema name, from any realm's answer (what it may
 *  write and what it may not, together): ["create diary-entry"]. */
export function toolWrites(realms, toolId) {
    const key = toolId ?? '';
    const out = new Set();
    list(realms).forEach((r) => {
        list(r?.writes?.[key]).forEach((w) => out.add(w));
        list(r?.cannot_write?.[key]).forEach((w) => out.add(w));
    });
    return [...out];
}

/** One written thing as the sheet says it, the schema first: "diary-entry · create", "nodes · create". */
export function writeWord(entry) {
    const [verb, ...rest] = String(entry || '').trim().split(/\s+/);
    if (!verb) return '';
    return `${rest.join(' ') || 'nodes'} · ${verb}`;
}

/** A realm as a Picker option: its name, "(default)" when it is, and what it lets this tool write. */
export function realmOption(realm, toolId) {
    return {
        value: realm.id,
        label: realm.is_default ? `${realm.name} (default)` : realm.name,
        hint: realmHint(realm, toolId) || undefined,
    };
}

/** The realm a tool runs in when nothing else is chosen: the default, else the first; null when none. */
export const defaultRealm = (realms) => (list(realms).find((r) => r.is_default) || list(realms)[0] || null);

/** The realm a run runs in: the one chosen, while it is still offered;
 *  else the default; else the first. */
export function startRealm(realms, current) {
    const rs = list(realms);
    if (current && rs.some((r) => String(r.id) === String(current))) return current;
    return (rs.find((r) => r.is_default) || rs[0])?.id || '';
}

// ── what a tool asks for ─────────────────────────────────────────────────────

/** One tool's reads from setup-config `reads` ({ plugin, tools: { id: [[type, schemaId]] } }
 *  or a list): [{ key, any } | { key, schemaId, name }]. */
export function toolReads(reads, toolId) {
    if (!reads) return [];
    const raw = Array.isArray(reads) ? reads : [...list(reads.plugin), ...list(reads.tools?.[toolId ?? ''])];
    const seen = new Set();
    const out = [];
    raw.forEach((r) => {
        const type = Array.isArray(r) ? r[0] : r?.type;
        const id = Array.isArray(r) ? r[1] : r?.schema_id;
        if (type === 'plugin_output' || type === 'upstream') return;
        const any = type === 'any' || !id;
        const key = any ? '*' : String(id);
        if (seen.has(key)) return;
        seen.add(key);
        out.push(any ? { key, any: true } : { key, schemaId: String(id), name: (!Array.isArray(r) && (r?.schema_name || r?.name)) || null });
    });
    return out;
}

// ── a run's answer ───────────────────────────────────────────────────────────

/** A run's outcome from /execute's `execution` (its status and what it left out). */
export function executionOutcome(ex) {
    if (!ex) return 'unknown';
    if (ex.outcome_unknown) return 'unknown';
    const dropped = ex.receipt?.outputs_dropped || ex.outputs_dropped;
    return outcomeOf({
        status: ex.status === 'completed_dry_run' ? 'completed' : ex.status,
        output_summary: { outputs_dropped: dropped, cannot_write: ex.cannot_write },
    });
}

/** What a run did, from /execute's answer: { outcome, counts (only the
 *  non-zero), made, changed (by name), proposed, leftOut, cannotWrite, runId,
 *  replay, unknown, message }. */
export function receiptOf(ex) {
    const r = ex?.receipt || {};
    const made = list(r.nodes_created).length ? list(r.nodes_created) : list(ex?.output_nodes);
    const madeN = made.length || list(ex?.output_node_ids).length;
    const changed = list(r.nodes_edited);
    const removed = list(r.nodes_deleted).length;
    const proposed = list(r.proposals_created).length;
    return {
        outcome: executionOutcome(ex),
        counts: [
            madeN > 0 && { label: 'Made', n: madeN },
            changed.length > 0 && { label: 'Changed', n: changed.length },
            removed > 0 && { label: 'Removed', n: removed },
        ].filter(Boolean),
        made: made.filter((x) => x?.id).map((x) => ({ id: x.id, title: x.title || null })),
        changed: changed.filter((x) => x?.id).map((x) => ({ id: x.id, title: x.title || null })),
        proposed,
        leftOut: leftOutOf(r.outputs_dropped || ex?.outputs_dropped),
        cannotWrite: list(ex?.cannot_write),
        runId: ex?.run_id || null,
        replay: !!ex?.idempotent_replay,
        unknown: !!ex?.outcome_unknown || ex?.status === 'unknown',
        message: ex?.message || null,
    };
}

/** What a run in the feed did, from its summary (a member's is cut down,
 *  `hidden`): { made, changed, removed, proposed, leftOut, cannotWrite, hidden }. */
export function runCounts(summary) {
    const s = summary || {};
    const made = typeof s.output_count === 'number' ? s.output_count : list(s.output_node_ids).length;
    return {
        made,
        changed: list(s.nodes_edited).length || (typeof s.changed_count === 'number' ? s.changed_count : 0),
        removed: list(s.nodes_deleted).length,
        proposed: list(s.proposals_created).length || (typeof s.proposed_count === 'number' ? s.proposed_count : 0),
        leftOut: leftOutOf(s.outputs_dropped),
        cannotWrite: list(s.cannot_write),
        hidden: !!s.hidden,
    };
}

/** "What happened", in a line: the refusal, the error, its own words, what it made. */
export function happenedLine(run) {
    if (run.refused) {
        const why = run.refused.replace(/^Refused: /, '');
        return why[0].toUpperCase() + why.slice(1);
    }
    if (run.error) return run.error;
    if (run.why) return run.why;
    const c = runCounts(run.summary);
    const parts = [
        c.made > 0 && `Made ${c.made}`,
        c.changed > 0 && `changed ${c.changed}`,
        c.proposed > 0 && `proposed ${c.proposed}`,
        c.leftOut.length > 0 && `left out ${c.leftOut.reduce((n, x) => n + x.count, 0)}`,
        c.cannotWrite.length > 0 && !c.leftOut.length && 'left some out',
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
}

/** How long a run took: "3s", "2m 5s". */
export function tookWord(s) {
    if (s == null) return null;
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
}

// ── a parked run's standing ──────────────────────────────────────────────────

/** A request that is no longer waiting, in a line; null while it waits. */
export function requestStateLine(status) {
    switch (status) {
        case 'pending': return null;
        case 'approved':
        case 'executed': return 'Allowed. It ran.';
        case 'denied': return 'Declined. It didn’t run.';
        case 'expired': return 'It waited too long and expired. It didn’t run.';
        case 'cancelled': return 'Cancelled. It didn’t run.';
        default: return 'It isn’t waiting any more.';
    }
}

/** "Expires in 9m" (or "Expired"). */
export function expiresWord(at, now = Date.now()) {
    if (!at) return null;
    if (new Date(at).getTime() <= now) return 'Expired';
    return `Expires ${until(at, { now })}`;
}

// ── how often ────────────────────────────────────────────────────────────────

/** The How often picker's options, with the current one when it is not a preset ("Custom · …"). */
export function frequencyOptions(current) {
    const opts = FREQUENCIES.map((f) => ({ value: f.id, label: f.label }));
    const cur = String(current || '').trim();
    if (cur && !FREQUENCIES.some((f) => f.id === cur || f.cron === cur)) {
        opts.push({ value: cur, label: `Custom · ${describeCron(cur) || cur}` });
    }
    return opts;
}
/** The preset a stored frequency or cron is, by its id; else the cron itself. */
export function frequencyValue(freq, cron) {
    const f = FREQUENCIES.find((x) => x.id === freq || x.cron === String(cron || '').trim() || x.cron === String(freq || '').trim());
    return f ? f.id : (cron || freq || '');
}
/** A typed cron, checked: null when it reads, else what is wrong. */
export function cronProblem(cron) {
    const parts = String(cron || '').trim().split(/\s+/);
    if (parts.length !== 5) return 'It has five parts: minute, hour, day, month and weekday.';
    if (!parts.every((p) => /^[\d*/,\-a-zA-Z?]+$/.test(p))) return 'ofSelf can’t read that.';
    return null;
}

// ── the runs page's facets ───────────────────────────────────────────────────

/** A run's tool, for the Tool facet: one key per plugin and tool. */
export const toolKey = (run) => `${run.pluginId || ''}:${run.toolId ?? ''}`;
export const toolOfKey = (key) => String(key || '').split(':').slice(1).join(':');
export const pluginOfKey = (key) => String(key || '').split(':')[0];

export { sameTool, plural };
